/**
 * Bot commands written in square brackets anywhere in a post that mentions
 * the bot, e.g. `@shopbot [buy/apple/3]` or `@dicebot [2d6]`.
 *
 * Admins set each account's commands as a tree (see BotCommandSet on the
 * server, Administration > Bot commands). This file has everything that
 * works on that tree without React: finding the bracket the caret is in,
 * suggesting the next part, and checking the brackets of a post while it is
 * written.
 *
 * Every check here takes time in proportion to the length of what was
 * written: the checks run in the browser on every pause in typing, so
 * nothing that could take long (such as regular expressions from elsewhere)
 * may be added.
 */

export interface BotCommandNode {
  /** `pattern` (a regular expression) is no longer set; old ones count as text */
  type: 'keyword' | 'text' | 'number' | 'dice' | 'pattern';
  name?: string;
  description?: string;
  hint?: string;
  min?: number;
  max?: number;
  count_min?: number;
  count_max?: number;
  sides_min?: number;
  sides_max?: number;
  children?: BotCommandNode[];
}

export interface BotCommands {
  acct: string;
  commands: BotCommandNode[];
}

/** A command node together with the bot it belongs to */
interface OwnedNode {
  acct: string;
  node: BotCommandNode;
}

export interface BotCommandSuggestion {
  /** `keyword` can be inserted, the other kinds only explain the part */
  kind: BotCommandNode['type'];
  label: string;
  description?: string;
  /** Set when several bots are mentioned */
  acct?: string;
  node: BotCommandNode;
}

export interface BotCommandContext {
  suggestions: BotCommandSuggestion[];
  /** Where the part being written starts and ends in the text: from the
   * last `[` or `/` up to the caret, never past it */
  start: number;
  end: number;
}

/** What fits where something that does not was written */
export type BotCommandExpected =
  | { kind: 'keyword'; names: string[] }
  | { kind: 'number'; range: string }
  | { kind: 'dice'; range: string };

/**
 * - `unknown`: a typo of a command; `suggestion` is the bracket corrected
 * - `invalid`: a value that does not fit; `expected` says what does
 * - `missing`, `too_many`: parts missing or one too many; `formats` are the
 *   whole commands it can be, e.g. `[구매/아이템명/개수]`
 */
export type BotCommandIssue =
  | { kind: 'unknown'; block: string; suggestion: string }
  | { kind: 'invalid'; block: string; expected: BotCommandExpected[] }
  | { kind: 'missing'; block: string; formats: string[] }
  | { kind: 'too_many'; block: string; formats: string[] };

// At most this many formats are named in one notice
const MAX_FORMATS = 3;

const MAX_SUGGESTIONS = 8;

// Fixed, simple formats: checked in time proportional to their length
const NUMBER_RE = /^-?\d{1,9}$/;
const DICE_RE = /^(\d{1,4})[dD](\d{1,6})$/;

// @username or @username@domain, followed by something that ends it, so
// that nothing is looked up while the name is still being typed
const MENTION_RE = /(?:^|[^\w@/])@([a-z0-9_]+)(?:@([a-z0-9.-]+[a-z0-9]))?(?=[^\w@.-]|\.(?:\s|$))/giu;
const BLOCK_RE = /\[([^[\]\n]*)\]/g;

/** Usernames of local accounts mentioned in a text, in lower case */
export function mentionedUsernames(text: string, localDomain?: string) {
  const names = new Set<string>();

  for (const match of text.matchAll(MENTION_RE)) {
    const [, username, domain] = match;
    if (!username) continue;
    if (domain && domain.toLowerCase() !== localDomain?.toLowerCase()) continue;

    names.add(username.toLowerCase());
  }

  return [...names];
}

function topLevel(bots: BotCommands[]): OwnedNode[] {
  return bots.flatMap(({ acct, commands }) =>
    commands.map((node) => ({ acct, node })),
  );
}

const within = (value: number, low?: number, high?: number) =>
  (typeof low !== 'number' || value >= low) &&
  (typeof high !== 'number' || value <= high);

/** Written like dice (`2d6`), whatever the limits */
const looksLikeDice = (segment: string) => DICE_RE.test(segment);

function nodeAccepts(node: BotCommandNode, segment: string) {
  switch (node.type) {
    case 'keyword':
      return node.name?.toLowerCase() === segment.toLowerCase();
    case 'number':
      return (
        NUMBER_RE.test(segment) && within(Number(segment), node.min, node.max)
      );
    case 'dice': {
      const match = DICE_RE.exec(segment);
      return (
        !!match &&
        within(Number(match[1]), node.count_min, node.count_max) &&
        within(Number(match[2]), node.sides_min, node.sides_max)
      );
    }
    default:
      // `text`, and old `pattern` parts
      return segment.length > 0;
  }
}

/** The node a segment chooses: fixed words first, then numbers and dice, then text */
function findNode(level: OwnedNode[], segment: string) {
  const order: BotCommandNode['type'][] = [
    'keyword',
    'number',
    'dice',
    'text',
    'pattern',
  ];

  for (const type of order) {
    const found = level.find(
      ({ node }) => node.type === type && nodeAccepts(node, segment),
    );
    if (found) return found;
  }

  return undefined;
}

function childrenOf({ acct, node }: OwnedNode): OwnedNode[] {
  return (node.children ?? []).map((child) => ({ acct, node: child }));
}

function rangeText(low?: number, high?: number) {
  const from = typeof low === 'number' ? String(low) : '';
  const to = typeof high === 'number' ? String(high) : '';

  return from || to ? `${from}~${to}` : '';
}

function diceRange(node: BotCommandNode) {
  const count = rangeText(node.count_min, node.count_max);
  const sides = rangeText(node.sides_min, node.sides_max);

  return count || sides ? `${count || 'N'}d${sides || 'M'}` : '';
}

/** A part's name alone, e.g. `구매`, `아이템명`, `개수`, `NdM` */
export function partName(node: BotCommandNode) {
  switch (node.type) {
    case 'keyword':
      return node.name ?? '';
    case 'number':
      return node.hint ?? '#';
    case 'dice':
      return node.hint ?? 'NdM';
    default:
      return node.hint ?? '…';
  }
}

/** The numbers a number part allows, e.g. `1~999`, or '' without limits */
export function numberRange(node: BotCommandNode) {
  return node.type === 'number' ? rangeText(node.min, node.max) : '';
}

/** How a part is shown in the suggestion list, e.g. `구매`, `아이템명`, `개수 1~999`, `NdM 1~10d2~100` */
function labelFor(node: BotCommandNode) {
  const range =
    node.type === 'number'
      ? rangeText(node.min, node.max)
      : node.type === 'dice'
        ? diceRange(node)
        : '';

  return `${partName(node)}${range ? ` ${range}` : ''}`;
}

// What may follow the caret for it to be at the end of the part being
// written: nothing, a space or line break, or a bracket or separator
const PART_END_RE = /[\s/[\]]/;

/**
 * The suggestions for the bracket the caret is in, or null when the caret is
 * not inside a bracket of a mentioned bot's commands, or is in the middle of
 * a word (`[구|마`): only what is written before the caret is completed, so
 * nothing after it is ever replaced
 */
export function findBotCommandContext(
  text: string,
  caret: number,
  bots: BotCommands[],
): BotCommandContext | null {
  if (bots.length === 0) return null;

  const following = text.charAt(caret);
  if (following !== '' && !PART_END_RE.test(following)) return null;

  const before = text.slice(0, caret);
  const open = before.lastIndexOf('[');
  if (open < 0) return null;

  const inside = before.slice(open + 1);
  if (/[\]\n]/.test(inside)) return null;

  const segments = inside.split('/');
  const partial = segments.pop() ?? '';

  let level = topLevel(bots);

  for (const segment of segments) {
    const found = findNode(level, segment);
    if (!found) return null;
    level = childrenOf(found);
  }

  if (level.length === 0) return null;

  const severalBots = new Set(bots.map(({ acct }) => acct)).size > 1;
  const lowered = partial.toLowerCase();

  const keywords = level
    .filter(
      ({ node }) =>
        node.type === 'keyword' &&
        (node.name ?? '').toLowerCase().startsWith(lowered),
    )
    .slice(0, MAX_SUGGESTIONS);

  // Hints for free slots: always right after `[` or `/`, and while typing
  // only once the bracket is known to be a command, so that ordinary
  // brackets in the same post get no popup
  const isFirstPart = segments.length === 0;
  const showSlots =
    partial.length === 0 || (!isFirstPart && keywords.length === 0);
  const slots = showSlots
    ? level.filter(({ node }) => node.type !== 'keyword')
    : [];

  if (keywords.length === 0 && slots.length === 0) return null;

  const suggestions = [...keywords, ...slots].map(({ acct, node }) => ({
    kind: node.type,
    label: labelFor(node),
    description: node.description,
    acct: severalBots ? acct : undefined,
    node,
  }));

  return { suggestions, start: caret - partial.length, end: caret };
}

/**
 * Puts a chosen keyword into the text. Returns the new text and where the
 * caret goes: after a `/` when more parts follow, after the `]` otherwise.
 */
/**
 * What a chosen suggestion writes: a fixed word as it is; for parts written
 * freely, something to start from: the name of a text part (`아이템명`), the
 * lowest number allowed (1 without a lower limit), `ndm` for dice
 */
export function insertValueFor(node: BotCommandNode) {
  switch (node.type) {
    case 'keyword':
      return node.name ?? '';
    case 'number': {
      if (typeof node.min === 'number') return String(node.min);
      // Without a lower limit 1, unless the upper limit is below that
      return typeof node.max === 'number' && node.max < 1
        ? String(node.max)
        : '1';
    }
    case 'dice':
      return 'ndm';
    default:
      return node.hint ?? '';
  }
}

export function insertBotCommand(
  text: string,
  context: BotCommandContext,
  suggestion: BotCommandSuggestion,
) {
  const next = text[context.end];
  const hasChildren = (suggestion.node.children?.length ?? 0) > 0;

  let insert = insertValueFor(suggestion.node);

  if (hasChildren) {
    if (next !== '/') insert += '/';
  } else if (next !== ']') {
    insert += ']';
  }

  const newText = text.slice(0, context.start) + insert + text.slice(context.end);
  let caret = context.start + insert.length;

  // Step over a separator that was already there
  if ((hasChildren && next === '/') || (!hasChildren && next === ']')) {
    caret += 1;
  }

  return { text: newText, caret };
}

function distance(a: string, b: string) {
  const previous = Array.from({ length: b.length + 1 }, (_, i) => i);

  for (let i = 1; i <= a.length; i++) {
    let diagonal = previous[0] ?? 0;
    previous[0] = i;

    for (let j = 1; j <= b.length; j++) {
      const above = previous[j] ?? 0;
      previous[j] = Math.min(
        above + 1,
        (previous[j - 1] ?? 0) + 1,
        diagonal + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
      diagonal = above;
    }
  }

  return previous[b.length] ?? 0;
}

/** The keyword a mistyped word most likely meant, if it is close enough */
function closestKeyword(level: OwnedNode[], segment: string) {
  const word = segment.toLowerCase();
  if (word.length === 0) return undefined;

  let best: { name: string; score: number } | undefined;

  for (const { node } of level) {
    if (node.type !== 'keyword' || !node.name) continue;

    const name = node.name.toLowerCase();
    const allowed = Math.max(name.length, word.length) <= 4 ? 1 : 2;
    const score = distance(word, name);

    if (score <= allowed && (!best || score < best.score)) {
      best = { name: node.name, score };
    }
  }

  return best?.name;
}

function expectedAt(level: OwnedNode[]): BotCommandExpected[] {
  const expected: BotCommandExpected[] = [];
  const names = level
    .filter(({ node }) => node.type === 'keyword')
    .map(({ node }) => node.name ?? '');

  if (names.length > 0) expected.push({ kind: 'keyword', names });

  for (const { node } of level) {
    if (node.type === 'number') {
      expected.push({ kind: 'number', range: rangeText(node.min, node.max) });
    } else if (node.type === 'dice') {
      expected.push({ kind: 'dice', range: diceRange(node) });
    }
  }

  return expected;
}

/** The whole commands that go on from the parts written so far */
function formatsFrom(path: BotCommandNode[]) {
  const formats: string[] = [];

  const walk = (parts: BotCommandNode[]) => {
    if (formats.length > MAX_FORMATS) return;

    const last = parts[parts.length - 1];
    const children = last?.children ?? [];

    if (children.length === 0) {
      formats.push(`[${parts.map(partName).join('/')}]`);
    } else {
      for (const child of children) walk([...parts, child]);
    }
  };

  walk(path);

  return formats.length > MAX_FORMATS
    ? [...formats.slice(0, MAX_FORMATS), '…']
    : formats;
}

/** The bracket with one part replaced */
function withPart(segments: string[], index: number, part: string) {
  return `[${segments.map((segment, i) => (i === index ? part : segment)).join('/')}]`;
}

/**
 * Checks every bracket of a post. Brackets that do not start with one of the
 * bots' commands (and do not look like a typo of one) are ordinary brackets
 * and are left alone.
 */
export function checkBotCommands(text: string, bots: BotCommands[]) {
  const issues: BotCommandIssue[] = [];
  if (bots.length === 0) return issues;

  const top = topLevel(bots);

  for (const match of text.matchAll(BLOCK_RE)) {
    const block = match[0];
    const segments = (match[1] ?? '').split('/');
    const first = segments[0] ?? '';

    let current = findNode(top, first);

    // Dice outside the limits a bot set
    const dice = current
      ? undefined
      : top.find(({ node }) => node.type === 'dice' && looksLikeDice(first));

    if (dice) {
      issues.push({ kind: 'invalid', block, expected: expectedAt([dice]) });
      continue;
    }

    if (!current) {
      const suggestion = closestKeyword(top, first);
      if (suggestion) issues.push({ kind: 'unknown', block, suggestion: withPart(segments, 0, suggestion) });
      continue;
    }

    const path = [current.node];

    for (let i = 1; i <= segments.length; i++) {
      const level = childrenOf(current);
      const segment = segments[i];

      if (segment === undefined) {
        if (level.length > 0) issues.push({ kind: 'missing', block, formats: formatsFrom(path) });
        break;
      }

      if (level.length === 0) {
        issues.push({ kind: 'too_many', block, formats: formatsFrom(path) });
        break;
      }

      if (segment.length === 0) {
        issues.push({ kind: 'missing', block, formats: formatsFrom(path) });
        break;
      }

      const found = findNode(level, segment);

      if (!found) {
        const suggestion = closestKeyword(level, segment);
        issues.push(
          suggestion
            ? { kind: 'unknown', block, suggestion: withPart(segments, i, suggestion) }
            : { kind: 'invalid', block, expected: expectedAt(level) },
        );
        break;
      }

      current = found;
      path.push(found.node);
    }
  }

  return issues;
}
